import { Product, CreateProductPayload, UpdateProductPayload } from '../types/product';
import { ApiErrorResponse } from '../types/auth';
import { PaginationParams, PaginatedResponse, buildQueryString } from '../types/pagination';
import { STORE_ENDPOINTS, PRODUCT_ENDPOINTS } from '../utils/endPoints';
import { createApiClient } from '../utils/axiosConfig';
import axios from 'axios';

const apiClient = createApiClient();

export type { Product, CreateProductPayload, UpdateProductPayload };

export interface ProductWithStores extends Product {
  stores?: Array<{ id: number; store_name: string }>;
}

export interface GetAllProductsParams extends PaginationParams {
  sector_id?: number;
}

export const getAllProducts = async (params?: GetAllProductsParams): Promise<PaginatedResponse<ProductWithStores>> => {
  try {
    const qs = buildQueryString(params ?? {});
    const response = await apiClient.get<{ products: ProductWithStores[]; total: number; page: number; limit: number; totalPages: number }>(
      `${PRODUCT_ENDPOINTS.ALL}${qs}`
    );
    const d = response.data;
    return {
      items: d.products ?? [],
      total: d.total ?? 0,
      page: d.page ?? 1,
      limit: d.limit ?? 20,
      totalPages: d.totalPages ?? 0,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to fetch all products');
    }
    console.error('Get all products error:', error);
    throw new Error('An unexpected error occurred while fetching products.');
  }
};

export const getProductsByStore = async (storeId: number): Promise<Product[]> => {
  try {
    const response = await apiClient.get<any>(STORE_ENDPOINTS.GET_STORE_PRODUCTS(storeId));

    if (response.data.records && Array.isArray(response.data.records)) {
      const allProducts = response.data.records.flatMap((sector: any) =>
        sector.products.map((product: any) => ({
          ...product,
          name: product.name,
          description: product.description,
          image_url: product.image_url,
          sector_id: sector.id,
          sector: {
            id: sector.id,
            sector_name: sector.sector_name
          }
        }))
      );
      return allProducts;
    }

    return [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to fetch products');
    }
    console.error('Get products by store error:', error);
    throw new Error('An unexpected error occurred while fetching products.');
  }
};

export const getProductsBySector = async (storeId: number, sectorId: number): Promise<Product[]> => {
  try {
    const response = await apiClient.get<any>(STORE_ENDPOINTS.GET_STORE_PRODUCTS(storeId));

    if (response.data.records && Array.isArray(response.data.records)) {
      const sector = response.data.records.find((s: any) => s.id === sectorId);
      if (sector && sector.products) {
        return sector.products.map((product: any) => ({
          ...product,
          name: product.name,
          description: product.description,
          image_url: product.image_url,
          sector_id: sector.id,
          sector: {
            id: sector.id,
            sector_name: sector.sector_name
          }
        }));
      }
    }

    return [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to fetch sector products');
    }
    console.error('Get products by sector error:', error);
    throw new Error('An unexpected error occurred while fetching sector products.');
  }
};

export const createProduct = async (storeId: number, productData: CreateProductPayload, imageFile?: File): Promise<{ product: Product, message: string }> => {
  try {
    const formData = new FormData();
    formData.append('store_id', storeId.toString());

    Object.keys(productData).forEach(key => {
      const value = (productData as any)[key];
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    if (imageFile) {
      formData.append('imageFile', imageFile);
    }

    const response = await apiClient.post<{ product: Product, message: string }>(
      PRODUCT_ENDPOINTS.CREATE,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to create product');
    } else {
      console.error('Create product service error:', error);
      throw new Error('An unexpected error occurred while creating the product.');
    }
  }
};

export const updateProduct = async (productId: number, productData: UpdateProductPayload, imageFile?: File, storeId?: number): Promise<{ product: Product, message: string }> => {
  try {
    const formData = new FormData();
    formData.append('product_id', productId.toString());

    if (storeId) {
      formData.append('store_id', storeId.toString());
    }

    Object.keys(productData).forEach(key => {
      const value = (productData as any)[key];
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    if (imageFile) {
      formData.append('imageFile', imageFile);
    }

    const response = await apiClient.put<{ product: Product, message: string }>(
      PRODUCT_ENDPOINTS.UPDATE,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to update product');
    } else {
      console.error('Update product service error:', error);
      throw new Error('An unexpected error occurred while updating the product.');
    }
  }
};

export const deleteProduct = async (productId: number): Promise<{ message: string }> => {
  try {
    const response = await apiClient.delete<{ message: string }>(PRODUCT_ENDPOINTS.DELETE(productId));
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to delete product');
    } else {
      console.error('Delete product service error:', error);
      throw new Error('An unexpected error occurred while deleting the product.');
    }
  }
}; 